import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import CommodityIcon from 'features/shared/components/icons/CommodityIcon';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { isEmpty } from 'features/shared/helpers/objects';
import React from 'react';
import Select, { components, createFilter, SingleValueProps } from "react-select";
import { Option, Config } from 'react-select/src/filters';
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { Nullable } from 'features/shared/types/nullable';
import { useTranslation } from 'react-i18next';
import ContractStatusLabel from 'features/contracts/components/ContractStatusLabel';

interface Props {
  name: string;
  contracts: ContractDropdownDTO[];
  onChange: (contract: ContractDropdownDTO | null) => void;
  label?: string;
  placeholder?: string;
  errors?: Record<string, string>;
  defaultContract?: Nullable<ContractDropdownDTO>;
  type?: "Contract" | "PlaceOfDelivery";
}

// TODO: sjednotit s ContractSelectUnified a nechat pouze tuto komponentu (pro nove implementace pouzivat ContractSelectUnified)
const ContractSelect = ({
  name,
  contracts,
  onChange,
  label,
  placeholder,
  errors,
  defaultContract,
  type = "Contract",
}: Props) => {
   // translation
   const { t } = useTranslation("contracts\\contracts");
  
  // helpers
  const getSelectedItemText = (item: ContractDropdownDTO): string => {
    switch (type) {
      case "Contract":
        return item.contractNumber!;
      case "PlaceOfDelivery":
        return item.placeOfDelivery!;
      default:
        return "";
    }
  }

  // filter setting
  const filterConfig: Config = {
    stringify: (obj: any) => {
      const { contractNumber, placeOfDelivery, placeOfDeliveryAddress } = obj.data as ContractDropdownDTO;
      return `${contractNumber} ${placeOfDelivery} ${placeOfDeliveryAddress}`;
    }
  };

  // default value
  const getDefaultContract = () => {
    if (!isEmpty(defaultContract!)) {
      return defaultContract;
    }
  }

  // sub-renders
  const renderOption = (contract: ContractDropdownDTO) => {
    return (
      <div className="d-flex align-items-center">
        <CommodityIcon commodityDetail={contract.commodityDetail!} includeColor size="lg" />
        <div className="pl-2" style={{minWidth: 0}}>
            <div className="text-truncate">
              <span>{contract.placeOfDelivery}</span>
              <span className="mx-2 text-muted">|</span>
              <span className="text-muted">{contract.contractNumber}</span>
            </div>

          <div className="text-truncate mt-n1">
            {contract.contractStatus && (
              <ContractStatusLabel
                status={contract.contractStatus}
                asText
                className="mr-2"
              />
            )}
            <span className="small text-muted">
              {contract.placeOfDeliveryAddress}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const renderSingleValue = (props: SingleValueProps<ContractDropdownDTO>) => (
    <components.SingleValue {...props}>
      {getSelectedItemText(props.data)}
    </components.SingleValue>
  );

  // render
  return (
    <FormGroup 
      labelText={label ?? t(`contractSelect.${type}.Label`)} 
      validationError={errors ? errors[name] : undefined}>
      <Select
        options={contracts}
        getOptionLabel={(contract) => contract.contractNumber!}
        getOptionValue={(contract) => contract.contractId!}
        value={getDefaultContract()}
        onChange={(val) => onChange(val)}
        formatOptionLabel={(contract) => renderOption(contract)}
        components={{SingleValue: renderSingleValue}}
        filterOption={createFilter(filterConfig)}
        captureMenuScroll={false}
        className={reactSelectStyles.customSelect}
        classNamePrefix="customSelect"
        placeholder={placeholder ?? t(`contractSelect.${type}.Placeholder`)}
        noOptionsMessage={() => t(`contractSelect.${type}.NoOptions`)}
      />
    </FormGroup>
  )
}

export default ContractSelect;